import { useStepScopeProvider } from '../modules/contexts/step-scope-context/StepScopeContextProvider';
import { Steps } from '../modules/wizard/Steps';
import { Titles } from '../modules/wizard/Tiltes';
import { useFeatureServices } from '../use-feature-services';
import { v4 as uuidv4 } from 'uuid';
import { Partner, usePartner } from './UsePartner';
import { ApiError } from "../modules/async/api-client";

export interface TrackingService {
  trackButtonClick(data: VWBasic_Button_Click_Event): void;
  trackFormStepButtonClick(data: VWBasic_FormStepButton_Click ): void;
  trackStepPageLoad(data: VWBasic_Step_Pageload): void;
  trackPageLoad(data: VWBasic_Pageload_Event): void;
  trackStepButtonClick(data: VWBasic_StepButton_Click_Event): void;
  trackDealerSelectClick(data: VWBasic_DealerSelect_Click_Event): void;
  trackFormStepLoad(data: VWBasic_FormStep_Load): void;
  trackFormCTAClick(data: VWBasic_FormCTA_Click): void;
  trackFormSubmissionSuccessMessageLoad(data: VWBasic_FormSubmissionSuccessMessage_Load): void;
  trackFormPageLoad(data: VWBasic_Form_Pageload): void;
  trackFormFormErrorMessageLoad(data: VWBasic_FormErrorMessage_Load): void;
}

export interface VWBasic_Button_Click_Event {
  contentId: string;
  linkName: string;
  partner?: Partner;
  step?: Steps;
  form?: VWBasic_Form_Collection_Data;
}

export interface VWBasic_FormStepButton_Click  {
  form?: VWBasic_Form_Collection_Data;
}

export interface VWBasic_Pageload_Event {
  contentId: string;
}

export interface VWBasic_Step_Pageload {
  contentId: string;
}

export interface VWBasic_StepButton_Click_Event {
  contentId: string;
  partner?: Partner;
  step?: Steps;
  identityLabel?: string;
}

export interface VWBasic_DealerSelect_Click_Event {
  contentId: string;
  partner: Partner;
}

export interface VWBasic_FormCTA_Click {
  form?: VWBasic_Form_Collection_Data;
}

export interface VWBasic_FormSubmissionSuccessMessage_Load {
  form?: VWBasic_Form_Collection_Data;
}

export interface VWBasic_FormStep_Load {
  form?: VWBasic_Form_Collection_Data;
}

export interface VWBasic_Form_Pageload   {
  form?: VWBasic_Form_Collection_Data;
}

export interface VWBasic_FormErrorMessage_Load {
  contentId: string;
  form?: VWBasic_Form_Collection_Data;
  error: ApiError;
}

interface VWBasic_Process_Event_Data {
  StepNumber: number;
  StepName: string;
  processStart: boolean;
  processComplete: boolean;
}


export interface VWBasic_Form_Collection_Data {
  FormStart?: boolean;
  FormName?: string;
}

export function useTracking(): TrackingService {
  const { tracking } = useFeatureServices();
  const stepContext = useStepScopeProvider();
  const partner = usePartner();

  return {
    trackButtonClick: (data: VWBasic_Button_Click_Event): void => {
      const partnerToUse = data.partner ?? partner;
      const processToUse = stepContext.stepNumber ?? data.step;
      tracking.track('VWBasic_Button_Click', {
        eventInfo: createEventInfo({
          contentId: data.contentId,
        }),
        //@ts-ignore
        ...(partnerToUse ? createPartnerEventData(partnerToUse) : {}),
        ...(data?.form ? createFormCollectionData(data.form) : {}),
        ...(processToUse !== undefined ? createProcessEventData(processToUse, false) : {}),
        ...(data.linkName && data.linkName.trim() !== ''
          ? createLinkEventDataWithLinkName(data.linkName)
          : createLinkEventData(stepContext.stepNumber)),
      });
    },

    trackFormStepButtonClick: (data: VWBasic_FormStepButton_Click): void => {
      tracking.track('VWBasic_FormStepButton_Click', {
        eventInfo: createEventInfo({
        }),
        //@ts-ignore
        partner,
        ...data,
        ...createFormCollectionData(data?.form),
        ...(stepContext?.stepNumber !== undefined ? createProcessEventData(stepContext.stepNumber, false) : {}),
        ...createLinkEventData(stepContext.stepNumber),
      });
    },

    trackStepPageLoad: (data: VWBasic_Step_Pageload): void => {
      tracking.track('VWBasic_Step_Pageload', {
        eventInfo: createEventInfo({
          contentId: data.contentId,
        }),
        //@ts-ignore
        partner,
        ...(stepContext?.stepNumber !== undefined ? createProcessEventData(stepContext.stepNumber, false) : {}),
      });
    },

    trackPageLoad: (data: VWBasic_Pageload_Event): void => {
      tracking.track('VWBasic_Pageload', {
        eventInfo: createEventInfo({
          contentId: data.contentId,
        }),
        //@ts-ignore
        partner,
        ...(stepContext?.stepNumber !== undefined ? createProcessEventData(stepContext.stepNumber, false) : {}),
      });
    },

    trackStepButtonClick: (data: VWBasic_StepButton_Click_Event): void => {
      const partnerToUse = data.partner ?? partner;
      const processToUse = stepContext.stepNumber ?? data.step;
      tracking.track('VWBasic_StepButton_Click', {
        eventInfo: createEventInfo({
          contentId: data.identityLabel ?? Titles[data.step ?? stepContext.stepNumber] ?? 'SendConfirmationMessage',
        }),
        //@ts-ignore
        ...(partnerToUse ? createPartnerEventData(partnerToUse) : {}),
        ...(processToUse !== undefined ? createProcessEventData(processToUse, false) : {}),
        ...createStepButtonClickLinkEventData(data.contentId),
      });
    },

    trackDealerSelectClick: (data: VWBasic_DealerSelect_Click_Event): void => {
      tracking.track('VWBasic_DealerSelect_Click', {
        eventInfo: createEventInfo({
          contentId: data.contentId,
        }),
        partner: data.partner,
        ...(stepContext?.stepNumber !== undefined ? createProcessEventData(stepContext.stepNumber, false) : {}),
        ...createLinkEventData(stepContext.stepNumber),
      });
    },

    trackFormStepLoad: (data: VWBasic_FormStep_Load): void => {
      tracking.track('VWBasic_FormStep_Load', {
        eventInfo: createEventInfo({
        }),
        //@ts-ignore
        partner,
        ...data,
        ...createFormCollectionData(data?.form),
        ...(stepContext?.stepNumber !== undefined ? createProcessEventData(stepContext.stepNumber, false) : {}),
        ...createLinkEventData(stepContext.stepNumber),
      });
    },

    trackFormCTAClick: (data: VWBasic_FormCTA_Click): void => {
      tracking.track('VWBasic_FormCTA_Click', {
        eventInfo: createEventInfo({
        }),
        //@ts-ignore
        partner,
        ...data,
        ...createFormCollectionData(data?.form),
        ...(stepContext?.stepNumber !== undefined ? createProcessEventData(stepContext.stepNumber, false) : {}),
        ...createLinkEventData(stepContext.stepNumber),
      });
    },

    trackFormSubmissionSuccessMessageLoad: (data: VWBasic_FormSubmissionSuccessMessage_Load): void => {
      tracking.track('VWBasic_FormSubmissionSuccessMessage_Load', {
        eventInfo: createEventInfo({
        }),
        //@ts-ignore
        partner,
        ...data,
        ...createFormCollectionData(data?.form),
        ...createProcessEventData(stepContext?.stepNumber ?? {}, true),
        ...createLinkEventData(stepContext.stepNumber),
      });
    },

    trackFormPageLoad: (data: VWBasic_Form_Pageload): void => {
      tracking.track('VWBasic_Form_Pageload', {
        eventInfo: createEventInfo({
        }),
        //@ts-ignore
        partner,
        ...createFormCollectionData(data?.form),
        ...(stepContext?.stepNumber !== undefined ? createProcessEventData(stepContext.stepNumber, false) : {}),
        ...createLinkEventData(stepContext.stepNumber),
      })
    },

    trackFormFormErrorMessageLoad: (data: VWBasic_FormErrorMessage_Load): void => {
      tracking.track('VWBasic_FormErrorMessage_Load', {
        eventInfo: createEventInfo({
          contentId: data.contentId,
        }),
        //@ts-ignore
        partner,
        ...createFormCollectionData(data?.form),
        ...(stepContext.stepNumber !== undefined ? createProcessEventData(stepContext.stepNumber, false) : {}),
        error: {
          code: data.error.status,
          message: data.error.data?.detail ?? data.error.message,
        }
      })
  }
  };
}

function createProcessEventData(step: Steps | null, processComplete: boolean): { process: VWBasic_Process_Event_Data } | undefined {
  return step ? {
    process: {
      StepNumber: step,
      StepName: Titles[step] ?? 'Default Step Name',
      processStart: step === Steps.YourVehicle,
      processComplete: processComplete,
    }
  } : undefined
}

function createLinkEventData(stepNumber: Steps): { link: { url: string, name: string } } {
  return {
    link: {
      url: window.location.toString(),
      name: Titles[stepNumber] ?? 'SendConfirmationMessage'
    }
  }
}

function createStepButtonClickLinkEventData(contentId: string): { link: { url: string, name: string } } {
  return {
    link: {
      url: window.location.toString(),
      name: contentId
    }
  }
}

function createLinkEventDataWithLinkName(linkName: string): { link: { url: string, name: string } } {
  return {
    link: {
      url: window.location.toString(),
      name: linkName
    }
  }
}

function createPartnerEventData(partner: Partner): {
  partner: {
    informationName: string,
    informationBrand: string,
    informationZIP: string,
    informationBnr: string,
    informationCountry: string;
  } }
{
  return {
    partner: {
      informationName: partner.informationName,
      informationBrand: partner.informationBrand,
      informationZIP: partner.informationZIP,
      informationBnr: partner.informationBnr,
      informationCountry: partner.informationCountry
    }
  }
}

function createFormCollectionData(data?: VWBasic_Form_Collection_Data): {
  form: { FormTypeMultiple: string[]; FormName: string | undefined; FormStart: boolean; LeadID: string }
} {
  return {
    form: {
      FormName: "servicerequest",
      FormTypeMultiple: ["servicerequest"],
      FormStart: data?.FormStart ? true : false,
      LeadID: `ZA-${uuidv4().replace('-', '')}`,
    }
  }
}

function createEventInfo(extra: Record<string, any> = {}): { pageName: string } & Record<string, any> {
  return {
    pageName: document.title,
    ...extra,
  };
}
